import React, { useState, useContext, useEffect } from "react";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import SideBar from "components/common/SideBar/SideBarNew";
import Forms from "./Forms";
import PlatformAdminFormsList from "./PlatformAdminFormsList";
import { isNativePlatformAdminView } from "./platformAdminForms.utils";
import { useNavigate } from "react-router";
import { LoginContext } from "helpers/LoginContext";
import {
  ALL_FORM_LIST_LIMIT,
  emptyPaginationObj,
  SORT_TYPE,
} from "Constants/constant";
import { getPaginationObj } from "Utils/utils";
import {
  buildFormTemplateStatusFilter,
  resolveFormCategoryFilterIds,
} from "components/DynamicForms/shared/formCategoryUtils";
import { LOADER_STATUS } from "store/constants";

const AllForms = (props) => {
  const {
    categoryLoader,
    fetchFormTemplate,
    fetchFormCategories,
    formCategoriesList,
    spFormTemplatesList,
    spFormTemplateLoader,
    clearCrmHrmformstemplatesLocally,
    role,
  } = props;

  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);
  const [renderLoading, setRenderLoading] = useState(LOADER_STATUS.NA);
  const navigate = useNavigate();
  const [routeroleAccess, setRouteroleAccess] = useState(null);
  const isPlatformAdminView = isNativePlatformAdminView();

  useEffect(() => {
    if (routeroleAccess && routeroleAccess !== role) {
      window.location.reload();
    }
  }, [role]);

  useEffect(() => {
    setRouteroleAccess(role);
    setRenderLoading(LOADER_STATUS.PENDING);
    fetchFormCategories({ role });

    return () => {
      if (clearCrmHrmformstemplatesLocally) {
        clearCrmHrmformstemplatesLocally();
      }
    };
  }, [role]);

  const [paginationObj, setPaginationObj] = useState(
    spFormTemplatesList
      ? getPaginationObj(
          spFormTemplatesList.form_templates,
          spFormTemplatesList.query_params
        )
      : emptyPaginationObj
  );
  const [sortBy, setSortBy] = useState(null);

  useEffect(() => {
    if (spFormTemplatesList) {
      setPaginationObj(
        getPaginationObj(
          spFormTemplatesList.form_templates,
          spFormTemplatesList.query_params
        )
      );
    }
  }, [spFormTemplatesList]);

  const editFormTemplateHandler = (item) => {
    const url = role === "hrm" ? "/edit-template/hrm" : "/edit-template";
    navigate(`${url}/${item.slug}`, {
      state: { templateUUID: item.uuid },
    });
  };

  const fetchFormTemplateList = async (filters) => {
    if (!filters || filters === undefined) {
      Object.assign((filters = {}), {
        page: paginationObj.page,
      });
    }
    Object.assign(filters, { per_page: ALL_FORM_LIST_LIMIT });

    // Build bodyFilter the same way SilForms does: include status inside bodyFilter
    const bodyFilter = filters.hasOwnProperty("filter")
      ? { ...filters.filter, status: filters.status }
      : null;

    if (filters.hasOwnProperty("filter")) {
      delete filters.filter;
    }

    // keep passing role because AllForms flow relies on it
    fetchFormTemplate({ filters, bodyFilter, role });
  };

  useEffect(() => {
    if (!categoryLoader && renderLoading === LOADER_STATUS.PENDING) {
      setRenderLoading(LOADER_STATUS.COMPLETE);
    }
  }, [categoryLoader]);

  const handleFetch = (obj, sortBy) => {
    // ensure page default = 1
    if (!obj.page) {
      obj.page = 1;
    }

    if (sortBy) {
      Object.assign(obj, {
        sort_field: paginationObj.sort_field ? paginationObj.sort_field : null,
        sort_order:
          paginationObj.sort_order === SORT_TYPE.DESC
            ? SORT_TYPE.DESC
            : SORT_TYPE.ASC,
      });
    }

    if (paginationObj.page || obj?.page) {
      Object.assign(obj, { page: obj.page ? obj.page : paginationObj.page });
    }

    if (paginationObj.sort_field && !obj.hasOwnProperty("sort_field")) {
      Object.assign(obj, {
        sort_field: paginationObj.sort_field ? paginationObj.sort_field : null,
        sort_order:
          paginationObj.sort_order === SORT_TYPE.DESC
            ? SORT_TYPE.DESC
            : SORT_TYPE.ASC,
      });
    }

    if (obj.hasOwnProperty("search")) {
      Object.assign(obj, { page: 1, search: obj.search });
    }

    if (paginationObj.search && !obj.hasOwnProperty("search")) {
      Object.assign(obj, { search: paginationObj.search });
    }

    const categoryIds = resolveFormCategoryFilterIds({
      explicitSelection: obj.hasOwnProperty("form_category")
        ? obj.form_category
        : undefined,
      persistedSelection: paginationObj.filter?.form_category,
    });

    if (obj.hasOwnProperty("status")) {
      const filterData = buildFormTemplateStatusFilter(obj.status);

      if (categoryIds?.length > 0) {
        filterData.form_category = categoryIds;
      }

      Object.assign(obj, {
        page: obj.page ? obj.page : 1,
        status: obj.status,
        filter: filterData,
      });
    } else if (categoryIds?.length > 0) {
      Object.assign(obj, {
        filter: {
          ...(obj.filter || {}),
          form_category: categoryIds,
        },
      });
    }

    fetchFormTemplateList({ ...obj });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [paginationObj.page]);

  const resetAndFetch = (obj, sortBy = null) => {
    setPaginationObj((prev) => ({
      ...prev,
      page: 1,
    }));

    const newObj = { ...obj, page: 1 };

    handleFetch(newObj, sortBy);
  };

  const formsProps = {
    editFormTemplateHandler,
    templateList: spFormTemplatesList ? spFormTemplatesList.form_templates.data : [],
    isLoading: spFormTemplateLoader,
    queryParams: paginationObj,
    formCategoriesList,
    handleFetch,
    getSortBy: (value) => setSortBy(value),
    paginationObj,
    setPaginationObj,
    routeRole: role,
    isUserGracePeriodOver,
    resetAndFetch,
    fetchFormCategories,
  };

  const categoriesReady = !categoryLoader && renderLoading !== LOADER_STATUS.PENDING;

  const loadingOverlay = !categoriesReady ? (
    <div className="absolute top-1/2 z-50 flex h-full w-full flex-1 -translate-y-1/2 flex-col justify-center bg-blue-dark bg-opacity-20 px-4 py-12 sm:px-6 lg:flex lg:justify-center lg:px-20 xl:px-24">
      <CircularLoader classes="flex justify-center items-center" classloader="loader-l" />
    </div>
  ) : null;

  if (isPlatformAdminView) {
    return (
      <div className="relative min-h-full">
        {loadingOverlay}
        {categoriesReady ? <PlatformAdminFormsList {...formsProps} /> : null}
      </div>
    );
  }

  return (
    <>
      <div className='min-h-full'>
        {loadingOverlay}
        <SideBar />
        <div
          className={`${
            sideBarPadding
              ? "lg:pl-[4rem] duration-300"
              : "lg:pl-64 duration-300"
          } flex flex-col`}
        >
          {categoriesReady ? <Forms {...formsProps} /> : null}
        </div>
      </div>
    </>
  );
};

export default AllForms;
